import React, { useEffect, useState, useContext, useCallback } from "react";
import {
  AreaChart,
  ResponsiveContainer,
  XAxis,
  Tooltip,
  Legend,
  Area,
  Brush,
  CartesianGrid,
  YAxis
} from "recharts";
import RoomContext from '../RoomContext';
import { useRoomApi } from '../hooks/useRoomApi';
import "./Graph.css";

// How often the chart refetches to pull in newly logged readings and scroll the window forward.
const REFRESH_MS = 60000;

// Fetch windows for the history chart. Bounded by the backend's 60-day retention.
const WINDOWS = [
  { label: '6 h', hours: 6 },
  { label: '24 h', hours: 24 },
  { label: '7 días', hours: 24 * 7 },
  { label: '30 días', hours: 24 * 30 },
];

const formatTimeStamp = (timestamp) => {
  const date = new Date(timestamp);
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

// The brush travellers show a date/time appropriate to the window: within a day just the time, otherwise
// day + time so multi-day ranges are legible.
const formatBrushTick = (timestamp) => {
  const date = new Date(timestamp);
  const time = `${date.getHours()}:${date.getMinutes().toString().padStart(2, "0")}`;
  return `${date.getDate()}/${date.getMonth() + 1} ${time}`;
};

const formatTemperature = (temp) => `${Number(temp).toFixed(1)}°C`;

// Position rows are written only when the heater moves, so they're sparse; carry the last known
// position forward to every (denser) temperature sample for a clean step line.
const carryForwardPosition = (positionData) => {
  const sorted = positionData
    .map((p) => ({ time: Date.parse(p.timestamp), value: p.value }))
    .sort((a, b) => a.time - b.time);
  return (time) => {
    let value = null;
    for (const p of sorted) {
      if (p.time <= time) value = p.value;
      else break;
    }
    return value;
  };
};

export default function Graph() {
  const { tenant, room } = useContext(RoomContext);
  const api = useRoomApi(tenant, room);
  const [graphData, setGraphData] = useState([]);
  const [windowHours, setWindowHours] = useState(24);
  // Auto-refresh is paused while the user has zoomed/panned the brush, so a refetch doesn't yank the
  // view back to the full window mid-inspection. It resumes once the brush covers the full range again.
  const [zoomed, setZoomed] = useState(false);

  const fetchData = useCallback(async () => {
    const to = new Date();
    const from = new Date(to.getTime() - windowHours * 60 * 60 * 1000);
    const [temperatureData, positionData] = await Promise.all([
      api.getHistory('temperature', from.toISOString(), to.toISOString()),
      api.getHistory('position', from.toISOString(), to.toISOString()),
    ]);

    const positionAt = carryForwardPosition(positionData);
    setGraphData(temperatureData.map((p) => {
      const time = Date.parse(p.timestamp);
      return { time, temperature: p.value, position: positionAt(time) };
    }));
  }, [api, windowHours]);

  // Load on mount and whenever the window changes.
  useEffect(() => {
    fetchData().catch((e) => console.error('history load failed', e));
  }, [fetchData]);

  // Periodic refresh: skip while zoomed or while the tab is hidden, and refresh immediately on return.
  useEffect(() => {
    const refresh = () => {
      if (document.hidden || zoomed) return;
      fetchData().catch((e) => console.error('history refresh failed', e));
    };
    const id = setInterval(refresh, REFRESH_MS);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [fetchData, zoomed]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="recharts-default-tooltip custom-tooltip">
          <p className="label">{`Hora: ${formatTimeStamp(label)}`}</p>
          {payload[0] && <p className="label">{`Temperatura: ${formatTemperature(payload[0].value)}`}</p>}
          {payload[1] && <p className="label">{`Posición: ${Math.round(payload[1].value)}`}</p>}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="graph-container mt-4">
      <ResponsiveContainer width="100%" height={290}>
      <AreaChart data={graphData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id="tempGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="orange" />
            <stop offset="50%" stopColor="yellow" />
            <stop offset="100%" stopColor="green" />
          </linearGradient>
          <linearGradient id="orangeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="orange" stopOpacity={0.2} />
            <stop offset="40%" stopColor="white" stopOpacity={0.2} />
          </linearGradient>
          <linearGradient id="blueGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#0045EA" stopOpacity={0.2} />
            <stop offset="40%" stopColor="white" stopOpacity={0.2} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="time" type="number" scale="time" tickFormatter={formatTimeStamp} domain={['auto', 'auto']} axisLine={false} />
        <Tooltip content={<CustomTooltip />} />
        <Legend formatter={(value) => <span className="legend-text">{value}</span>} />

        <YAxis domain={[10, 30]} axisLine={false} tick={false} hide={true} />
        <Area type="monotone" dataKey="temperature" stroke="url(#tempGradient)" fill="url(#orangeGradient)" name="Temperatura" connectNulls />

        <YAxis yAxisId="right" orientation="right" domain={[1, 3]} hide={true} />
        <Area type="stepAfter" dataKey="position" stroke="#0045EA" fill="url(#blueGradient)" name="Posición" yAxisId="right" connectNulls />

        <Brush
          dataKey="time"
          height={22}
          travellerWidth={8}
          stroke="#0045EA"
          tickFormatter={formatBrushTick}
          onChange={(r) => setZoomed(!(r.startIndex === 0 && r.endIndex === graphData.length - 1))}
        />
      </AreaChart>
      </ResponsiveContainer>
      <div className="btn-group btn-group-sm d-flex mt-2" role="group" aria-label="Rango de tiempo">
        {WINDOWS.map(({ label, hours }) => (
          <button
            key={hours}
            type="button"
            className={`btn btn-outline-secondary${windowHours === hours ? ' active' : ''}`}
            onClick={() => setWindowHours(hours)}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
